import BarcodeExample from './BarcodeExample';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import Quagga from 'quagga';
import { normalizeBarcodeToISBN, isValidBarcode } from '../utils/isbnUtils';

interface BarcodeScannerProps {
  onScan: (code: string) => void;
  onError?: (error: string) => void;
}

const BarcodeScanner: React.FC<BarcodeScannerProps> = ({ onScan, onError }) => {
  const scannerRef = useRef<HTMLDivElement>(null);
  const [isScanning, setIsScanning] = useState(false);
  const [error, setError] = useState<string>('');
  const [scanStatus, setScanStatus] = useState<string>('');
  const scannedRef = useRef<Set<string>>(new Set());
  const lastScanTimeRef = useRef<number>(0);

  const handleSuccessfulScan = useCallback((isbn: string) => {
    setScanStatus(`ISBN detected: ${isbn}`);
    setError('');
    
    // Stop scanning after successful detection
    setTimeout(() => {
      Quagga.stop();
      setIsScanning(false);
      onScan(isbn);
    }, 500);
  }, [onScan]);

  useEffect(() => {
    if (!isScanning || !scannerRef.current) return;

    // Better camera constraints for barcode scanning
    const cameraConstraints: any = {
      facingMode: 'environment', // Use back camera on mobile
      // Use ideal constraints for better quality
      width: { ideal: 1280, min: 640 },
      height: { ideal: 720, min: 480 },
    };

    // Optimized Quagga configuration for ISBN/EAN barcodes
    Quagga.init(
      {
        inputStream: {
          name: 'Live',
          type: 'LiveStream',
          target: scannerRef.current,
          constraints: cameraConstraints,
          area: { // Define scanning area (percentage of video)
            top: '20%',
            right: '10%',
            left: '10%',
            bottom: '20%',
          },
        },
        locator: {
          patchSize: 'large', // Larger patch for better detection
          halfSample: false, // Full resolution for better accuracy
        },
        numOfWorkers: 4, // More workers for better performance
        frequency: 10, // Check every 10 frames
        decoder: {
          readers: [
            'ean_reader', // EAN-13 (ISBN-13)
            'ean_8_reader', // EAN-8
            'upc_reader', // UPC-A
            'upc_e_reader', // UPC-E
            // Remove code_128 and code_39 as they're not used for ISBNs
          ],
          debug: {
            drawBoundingBox: true,
            showFrequency: false,
            drawScanline: true,
            showPattern: false,
          },
        },
        locate: true,
      },
      (err) => {
        if (err) {
          console.error('Quagga initialization error:', err);
          setError('Failed to initialize camera. Please check permissions and try again.');
          if (onError) {
            onError('Failed to initialize camera');
          }
          setIsScanning(false);
          return;
        }
        
        // Add visual feedback during scanning
        Quagga.onProcessed((result) => {
          const drawingCtx = Quagga.canvas.ctx.overlay;
          const drawingCanvas = Quagga.canvas.dom.overlay;

          if (result) {
            const code = result.codeResult;
            if (code) {
              // Draw bounding box
              if (code.box) {
                Quagga.ImageDebug.drawPath(code.box, { x: 0, y: 1 }, drawingCtx, {
                  color: '#00F',
                  lineWidth: 2,
                });
              }
              // Draw scanline
              if (code.line) {
                Quagga.ImageDebug.drawPath(code.line, { x: 'x', y: 'y' }, drawingCtx, {
                  color: '#0F0',
                  lineWidth: 3,
                });
              }
            }
          } else {
            // Clear canvas when no code detected
            drawingCtx.clearRect(0, 0, parseInt(drawingCanvas.getAttribute('width') || '0'), parseInt(drawingCanvas.getAttribute('height') || '0'));
          }
        });

        setScanStatus('Camera ready. Position the ISBN barcode within the frame.');
        Quagga.start();
      }
    );

    Quagga.onDetected((result) => {
      const rawCode = result.codeResult.code;
      const format = result.codeResult.format;
      const now = Date.now();
      
      // Prevent duplicate scans within 1 second
      if (scannedRef.current.has(rawCode) || (now - lastScanTimeRef.current) < 1000) {
        return;
      }
      
      lastScanTimeRef.current = now;
      scannedRef.current.add(rawCode);
      setTimeout(() => {
        scannedRef.current.delete(rawCode);
      }, 3000);

      // Log the raw scanned code for debugging
      console.log('Raw scanned code:', rawCode, 'Format:', format);
      setScanStatus(`Detected: ${rawCode} (${format})`);
      
      // Validate and normalize the barcode
      if (!isValidBarcode(rawCode)) {
        setError(`Invalid barcode format: ${rawCode}. Please try scanning again.`);
        setScanStatus('');
        return;
      }

      // Prioritize EAN-13 (ISBN-13) format
      if (format === 'ean_13' && rawCode.length === 13) {
        // Direct ISBN-13, use as-is
        console.log('Direct ISBN-13 detected:', rawCode);
        handleSuccessfulScan(rawCode);
        return;
      }

      // Try to convert to ISBN-13
      const normalizedISBN = normalizeBarcodeToISBN(rawCode);
      
      if (!normalizedISBN) {
        console.warn(`Could not convert barcode "${rawCode}" to ISBN-13. Format: ${format}`);
        setError(`Could not convert barcode "${rawCode}" to ISBN. Please scan the ISBN-13 barcode (13 digits, usually starts with 978) or use manual entry.`);
        setScanStatus('');
        return;
      }
      
      // Warn if the converted ISBN looks suspicious
      if (normalizedISBN.startsWith('9780000') || normalizedISBN.startsWith('97800000')) {
        console.warn(`Converted ISBN "${normalizedISBN}" from "${rawCode}" looks suspicious.`);
        setError(`Warning: The scanned code "${rawCode}" may not be the ISBN barcode. Please scan the ISBN-13 barcode (13 digits, usually starts with 978) or use manual entry.`);
        setScanStatus('');
        return;
      }
      
      console.log('Normalized ISBN:', normalizedISBN);
      handleSuccessfulScan(normalizedISBN);
    });

    return () => {
      Quagga.stop();
      Quagga.offDetected();
      Quagga.offProcessed();
    };
  }, [isScanning, onScan, onError, handleSuccessfulScan]);

  const startScanning = () => {
    setError('');
    setScanStatus('');
    scannedRef.current.clear();
    lastScanTimeRef.current = 0;
    setIsScanning(true);
  };

  const stopScanning = () => {
    Quagga.stop();
    setIsScanning(false);
    setScanStatus('');
    setError('');
  };

  return (
    <div className="space-y-3 sm:space-y-4">
      <div className="flex gap-2 sm:gap-3">
        {!isScanning ? (
          <button
            type="button"
            onClick={startScanning}
            className="flex-1 bg-blue-600 text-white px-4 py-3 sm:py-2.5 rounded-md active:bg-blue-700 transition-colors text-sm sm:text-base font-medium min-h-[44px] touch-manipulation"
          >
            Start Camera Scanner
          </button>
        ) : (
          <button
            type="button"
            onClick={stopScanning}
            className="flex-1 bg-red-600 text-white px-4 py-3 sm:py-2.5 rounded-md active:bg-red-700 transition-colors text-sm sm:text-base font-medium min-h-[44px] touch-manipulation"
          >
            Stop Scanner
          </button>
        )}
      </div>

      {scanStatus && (
        <div className={`rounded-md p-3 sm:p-4 flex items-center gap-2 ${
          scanStatus.startsWith('ISBN detected') 
            ? 'bg-green-50 border border-green-200' 
            : 'bg-sky-50 border border-sky-200'
        }`}>
          {scanStatus.startsWith('ISBN detected') && (
            <svg className="w-5 h-5 text-green-600 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          )}
          <p className={`text-sm ${
            scanStatus.startsWith('ISBN detected') 
              ? 'text-green-800' 
              : 'text-sky-800'
          }`}>
            {scanStatus}
          </p>
        </div>
      )}

      {error && (
        <div className="rounded-md bg-red-50 border border-red-200 p-3 sm:p-4">
          <p className="text-sm text-red-800 whitespace-pre-line">{error}</p>
        </div>
      )}

      {/* Where to find the barcode */}
      <div className="flex items-center gap-3 rounded-md border border-gray-200 bg-gray-50 p-3">
        <BarcodeExample className="w-28 sm:w-36 flex-shrink-0" />
        <div className="text-sm text-gray-700">
          <p className="font-semibold text-gray-900">Where is the barcode?</p>
          <p className="mt-1">On the <span className="font-medium">back cover</span>, usually in a bottom corner, above the price.</p>
          <p className="mt-1 text-xs text-gray-500">It is the 13-digit number starting with 978 or 979 — not a QR code.</p>
        </div>
      </div>

      {isScanning && (
        <div className="relative w-full bg-black rounded-lg overflow-hidden">
          <div
            ref={scannerRef}
            className="w-full"
            style={{ 
              minHeight: window.innerWidth < 768 ? '300px' : '400px',
              aspectRatio: '16/9'
            }}
          />
          {/* Scanning area overlay */}
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <div className="relative">
              {/* Outer frame */}
              <div className="border-2 border-blue-500 rounded-lg w-64 sm:w-80 h-32 sm:h-40 shadow-lg" />
              {/* Corner indicators */}
              <div className="absolute -top-1 -left-1 w-6 h-6 border-t-4 border-l-4 border-blue-500 rounded-tl-lg" />
              <div className="absolute -top-1 -right-1 w-6 h-6 border-t-4 border-r-4 border-blue-500 rounded-tr-lg" />
              <div className="absolute -bottom-1 -left-1 w-6 h-6 border-b-4 border-l-4 border-blue-500 rounded-bl-lg" />
              <div className="absolute -bottom-1 -right-1 w-6 h-6 border-b-4 border-r-4 border-blue-500 rounded-br-lg" />
              {/* Scanning line animation */}
              <div className="absolute top-0 left-0 right-0 h-0.5 bg-blue-500 animate-pulse" style={{ animation: 'scanline 2s linear infinite' }} />
            </div>
          </div>
          {/* Instructions */}
          <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-75 text-white p-3 text-center">
            <p className="text-xs sm:text-sm font-medium">
              Position the ISBN-13 barcode within the frame
            </p>
            <p className="text-xs text-gray-300 mt-1">
              Look for the 13-digit barcode (usually starts with 978)
            </p>
          </div>
        </div>
      )}

      <style>{`
        @keyframes scanline {
          0% { transform: translateY(0); opacity: 1; }
          50% { opacity: 0.5; }
          100% { transform: translateY(160px); opacity: 1; }
        }
      `}</style>
    </div>
  );
};

export default BarcodeScanner;
